import React from 'react';
import * as Animatable from 'react-native-animatable';
import { Animated, ImageProps, TextProps, ViewProps } from 'react-native';
import { AnimatableProperties } from 'react-native-animatable';
import FastImage, { FastImageProps } from 'react-native-fast-image';

function select(...args: any[]) {
    for (const arg of args) {
        if (arg !== undefined) {
            return arg;
        }
    }
}

function defined(obj: any) {
    const copy: any = {};
    for (const k in obj) {
        if (obj[k] !== undefined) {
            copy[k] = obj[k];
        }
    }
    return copy;
}

function withAnimation(animation: { from: any, to: any }, { loop }: { loop?: boolean }) {
    if (loop) {
        return {
            [0]: defined(animation.from),
            [0.5]: defined(animation.to),
            [1]: defined(animation.from),
        } as AnimatableProperties<{}>['animation'];
    }
    return {
        from: defined(animation.from),
        to: defined(animation.to),
    } as AnimatableProperties<{}>['animation'];
}

export function seconds(secs: number) {
    return secs * 1000;
}

type AnimatedViewProps = {
    fromHeight?: number | string
    toHeight?: number | string
    fromWidth?: number | string
    toWidth?: number | string
    fromBorderColor?: string
    toBorderColor?: string
    fromScale?: number
    toScale?: number
    fromOpacity?: number
    toOpacity?: number
    fromBackgroundColor?: string,
    toBackgroundColor?: string
    fromTranslateY?: number
    toTranslateY?: number
    fromTranslateX?: number
    toTranslateX?: number
    fromRotateZ?: string
    toRotateZ?: string
    fromTop?: number | string
    toTop?: number | string
    fromLeft?: number | string
    toLeft?: number | string
    loop?: boolean
    duration?: number
    delay?: number
    animation?: AnimatableProperties<{}>['animation']
    enabled?: boolean
    easing?: AnimatableProperties<{}>['easing']
}

export function AnimatedView({
    fromHeight,
    toHeight,
    fromWidth,
    toWidth,
    fromBorderColor,
    toBorderColor,
    fromScale,
    toScale,
    fromOpacity,
    toOpacity,
    fromBackgroundColor,
    toBackgroundColor,
    fromTranslateY,
    toTranslateY,
    fromTranslateX,
    toTranslateX,
    fromRotateZ,
    toRotateZ,
    fromTop,
    toTop,
    fromLeft,
    toLeft,
    loop,
    duration,
    delay,
    animation,
    enabled = true,
    ...props
}: ViewProps & AnimatedViewProps) {
    if (!enabled) {
        return <Animatable.View {...props} />;
    }
    return (
        <Animatable.View
            animation={animation ?? withAnimation({
                from: {
                    height: select(fromHeight, toHeight),
                    borderColor: select(fromBorderColor, toBorderColor),
                    scale: select(fromScale, toScale),
                    width: select(fromWidth, toWidth),
                    opacity: select(fromOpacity, toOpacity),
                    backgroundColor: select(fromBackgroundColor, toBackgroundColor),
                    translateY: select(fromTranslateY, toTranslateY),
                    translateX: select(fromTranslateX, toTranslateX),
                    rotateZ: select(fromRotateZ, toRotateZ),
                    top: select(fromTop, toTop),
                    left: select(fromLeft, toLeft),
                },
                to: {
                    height: select(toHeight, fromHeight),
                    borderColor: select(toBorderColor, fromBorderColor),
                    scale: select(toScale, fromScale),
                    width: select(toWidth, fromWidth),
                    opacity: select(toOpacity, fromOpacity),
                    backgroundColor: select(toBackgroundColor, fromBackgroundColor),
                    translateY: select(toTranslateY, fromTranslateY),
                    translateX: select(toTranslateX, fromTranslateX),
                    rotateZ: select(toRotateZ, fromRotateZ),
                    top: select(toTop, fromTop),
                    left: select(toLeft, fromLeft),
                },
            }, { loop })}
            iterationCount={loop ? 'infinite' : undefined}
            duration={duration}
            delay={delay}
            {...props}
        />
    );
}


export function AnimatedFastImage(props: FastImageProps & AnimatedViewProps) {
    return (
        <AnimatedView {...props}>
            <FastImage {...props} />
        </AnimatedView>
    );
}

type AnimatedImageProps = {
    fromScale?: number
    toScale?: number
    fromRotateZ?: string
    toRotateZ?: string
    forBottom?: number | string
    toBottom?: number | string
    fromTop?: number | string
    toTop?: number | string
    duration?: number
    loop?: boolean
    delay?: number
    animation?: AnimatableProperties<{}>['animation']
    enabled?: boolean
}

export function AnimatedImage({
    fromScale,
    toScale,
    fromRotateZ,
    toRotateZ,
    forBottom,
    toBottom,
    fromTop,
    toTop,
    duration,
    loop,
    delay,
    animation,
    enabled = true,
    ...props
}: ImageProps & AnimatedImageProps) {
    if (!enabled) {
        return <Animatable.Image {...props} />;
    }
    return (
        <Animatable.Image
            animation={animation ?? withAnimation({
                from: {
                    scale: select(fromScale, toScale),
                    rotateZ: select(fromRotateZ, toRotateZ),
                    bottom: select(forBottom, toBottom),
                    top: select(fromTop, toTop),
                },
                to: {
                    scale: select(toScale, fromScale),
                    rotateZ: select(toRotateZ, fromRotateZ),
                    bottom: select(toBottom, forBottom),
                    top: select(toTop, fromTop),
                },
            }, { loop })}
            iterationCount={loop ? 'infinite' : undefined}
            duration={duration}
            delay={delay}
            {...props}
        />
    );
}



type AnimatedTextProps = {
    fromOpacity?: number
    toOpacity?: number
    fromColor?: string
    toColor?: string
    fromFontSize?: number
    toFontSize?: number
    duration?: number
    loop?: boolean
    delay?: number
    animation?: AnimatableProperties<{}>['animation']
    enabled?: boolean
}

export function AnimatedText({
    fromOpacity,
    toOpacity,
    fromColor,
    toColor,
    fromFontSize,
    toFontSize,
    duration,
    loop,
    delay,
    animation,
    enabled = true,
    ...props
}: TextProps & AnimatedTextProps) {
    if (!enabled) {
        return <Animatable.Text {...props} />;
    }
    return (
        <Animatable.Text
            animation={animation ?? withAnimation({
                from: {
                    opacity: select(fromOpacity, toOpacity),
                    color: select(fromColor, toColor),
                    fontSize: select(fromFontSize, toFontSize),
                },
                to: {
                    opacity: select(toOpacity, fromOpacity),
                    color: select(toColor, fromColor),
                    fontSize: select(toFontSize, fromFontSize),
                },
            }, { loop })}
            iterationCount={loop ? 'infinite' : undefined}
            duration={duration}
            delay={delay}
            {...props}
        />
    );
}

export function createClassComponent<P>(Component: React.ComponentType<P>) {
    class ClassComponent extends React.Component<P> {
        render(): React.ReactNode {
            return <Component {...this.props} />;
        }
    }
    return ClassComponent;
}

export function createAnimatedComponent<P>(Component: React.ComponentType<P>) {
    const AnimatedComponent = createClassComponent(Component);
    return Animated.createAnimatedComponent(AnimatedComponent);
}
